import { useEffect, useRef, useState } from "react";
import { useI18n, type DictKey } from "@/lib/i18n";
import { rateApi, type RateInfo } from "@/lib/api";
import "./site/landing.css";

const BOT_LINK = "https://t.me/ttex_robot";
const SITE_URL = "https://ttex.online/";
const THEME_KEY = "ttex_theme";
type Theme = "dark" | "light";

function initialTheme(): Theme {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch {
    /* ignore */
  }
  // Нет сохранённого выбора — идём за системной темой; фолбэк на тёмный (Signature Concept).
  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  }
  return "dark";
}

export function Landing() {
  const { lang, setLang, t } = useI18n();
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const [rate, setRate] = useState<RateInfo | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Реальный курс — тянем из API и обновляем раз в 30 с (не демо 84.17).
  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const r = await rateApi.current();
        if (alive) setRate(r);
      } catch {
        /* keep null */
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 30_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  // Плавный скролл по якорям, пока смонтирован лендинг.
  useEffect(() => {
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "smooth";
    return () => {
      html.style.scrollBehavior = prev;
    };
  }, []);

  // Эффекты примера: reveal при скролле, свечение под курсором, 3D-тилт.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cleanups: Array<() => void> = [];

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    root.querySelectorAll(".reveal").forEach((el) => obs.observe(el));
    cleanups.push(() => obs.disconnect());

    const glow = root.querySelector<HTMLElement>("#cursorGlow");
    if (glow) {
      const onMove = (e: PointerEvent) => {
        glow.style.left = e.clientX + "px";
        glow.style.top = e.clientY + "px";
      };
      window.addEventListener("pointermove", onMove);
      cleanups.push(() => window.removeEventListener("pointermove", onMove));
    }

    root.querySelectorAll<HTMLElement>("[data-tilt-zone]").forEach((zone) => {
      const onMove = (e: PointerEvent) => {
        const r = zone.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        zone.querySelectorAll<HTMLElement>(".tilt").forEach((el, i) => {
          const rx = -y * 8 + i * 1.5;
          const ry = x * 10 - i * 1.2;
          el.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg) ${el.classList.contains("card3d") ? "rotateZ(4deg)" : ""}`;
        });
      };
      const onLeave = () => zone.querySelectorAll<HTMLElement>(".tilt").forEach((el) => (el.style.transform = ""));
      zone.addEventListener("pointermove", onMove);
      zone.addEventListener("pointerleave", onLeave);
      cleanups.push(() => {
        zone.removeEventListener("pointermove", onMove);
        zone.removeEventListener("pointerleave", onLeave);
      });
    });

    root.querySelectorAll<HTMLElement>(".signature-card").forEach((el) => {
      const onMove = (e: PointerEvent) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        el.style.transform = `rotateX(${-y * 8}deg) rotateY(${x * 10}deg) rotateZ(-2deg)`;
      };
      const onLeave = () => (el.style.transform = "");
      el.addEventListener("pointermove", onMove);
      el.addEventListener("pointerleave", onLeave);
      cleanups.push(() => {
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerleave", onLeave);
      });
    });

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return (
    <div ref={rootRef} className="ttex-site" data-theme={theme}>
      <div id="cursorGlow" />

      <header className="header">
        <div className="container nav">
          <a className="brand" href="#top">
            TTEX
          </a>
          <nav className="nav-links">
            <a href="#about">{t("site_nav_about")}</a>
            <a href="#how">{t("site_nav_how")}</a>
            <a href="#calculator">{t("site_nav_calc")}</a>
            <a href="#payments">{t("site_nav_payments")}</a>
            <a href="#payout">{t("site_nav_payout")}</a>
            <a href="#card">{t("site_nav_card")}</a>
          </nav>
          <div className="nav-actions">
            <div className="lang-switch">
              {(["en", "ru"] as const).map((l) => (
                <button
                  key={l}
                  className={lang === l ? "active" : ""}
                  onClick={() => setLang(l)}
                  aria-pressed={lang === l}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <button
              className="icon-btn"
              onClick={() => setTheme((p) => (p === "dark" ? "light" : "dark"))}
              aria-label={theme === "dark" ? t("site_theme_light") : t("site_theme_dark")}
              title={theme === "dark" ? t("site_theme_light") : t("site_theme_dark")}
            >
              {theme === "dark" ? "☀" : "☾"}
            </button>
            <a className="btn primary" href={BOT_LINK} target="_blank" rel="noreferrer">
              {t("site_nav_open")}
            </a>
          </div>
        </div>
      </header>

      <main id="top">
        <section className="section hero">
          <div className="container hero-grid">
            <div className="hero-copy reveal">
              <span className="kicker">{t("site_hero_kicker")}</span>
              <h1>
                {t("site_hero_pre")}
                <span className="accent">{t("site_hero_accent")}</span>
                {t("site_hero_post")}
              </h1>
              <p>{t("site_hero_sub")}</p>
              <div className="hero-actions">
                <a className="btn primary" href={BOT_LINK} target="_blank" rel="noreferrer">
                  {t("site_hero_cta1")} ↗
                </a>
                <a className="btn ghost" href="#about">
                  {t("site_hero_cta2")} ↓
                </a>
              </div>
            </div>

            <div className="hero-visual reveal delay1" data-tilt-zone>
              <div className="orb">
                <i className="node n1" />
                <i className="node n2" />
                <i className="node n3" />
                <i className="node n4" />
                <i className="node n5" />
                <i className="node n6" />
                <i className="node n7" />
              </div>
              <div className="phone3d tilt">
                <div className="ui-brand">TTEX</div>
                <span className="ui-label">Внутренний счёт</span>
                <strong className="balance">₽ 1 250 000</strong>
                <span className="mini-btn">ПОПОЛНИТЬ</span>
                <div className="balance-row">
                  <span>USD</span>
                  <b>13 420.00</b>
                </div>
                <div className="balance-row">
                  <span>Заявки</span>
                  <b>3</b>
                </div>
              </div>
              <div className="card3d tilt">
                <div className="ui-brand">TTEX</div>
                <div className="card-sub">INTERNATIONAL</div>
                <div className="chip" />
                <div className="digits">•••• &nbsp;4821</div>
                <small>VIRTUAL / PHYSICAL</small>
              </div>
              <div className="route-pill">RUB → USD → WORLD</div>
            </div>
          </div>

          <div className="container hero-trust reveal delay2">
            <article>
              <div className="ico">◎</div>
              <div>
                <h3>{t("site_trust1_t")}</h3>
                <p>{t("site_trust1_d")}</p>
              </div>
            </article>
            <article>
              <div className="ico">▭</div>
              <div>
                <h3>{t("site_trust2_t")}</h3>
                <p>{t("site_trust2_d")}</p>
              </div>
            </article>
            <article>
              <div className="ico">▤</div>
              <div>
                <h3>{t("site_trust3_t")}</h3>
                <p>{t("site_trust3_d")}</p>
              </div>
            </article>
          </div>
        </section>

        <div className="marquee" aria-hidden="true">
          <div className="marquee-track">
            <span>RUB → USD → WORLD</span>
            <span>ACCOUNT</span>
            <span>SWIFT</span>
            <span>INVOICE</span>
            <span>TTEX CARD</span>
            <span>KYC</span>
            <span>PAYOUT</span>
            <span>RUB → USD → WORLD</span>
            <span>ACCOUNT</span>
            <span>SWIFT</span>
            <span>INVOICE</span>
            <span>TTEX CARD</span>
            <span>KYC</span>
            <span>PAYOUT</span>
          </div>
        </div>

        <section id="about" className="section">
          <div className="container section-head reveal">
            <span className="kicker">{t("site_about_kicker")}</span>
            <h2>{t("site_about_h2")}</h2>
            <p>{t("site_about_p")}</p>
          </div>
          <div className="container glass-grid">
            <article className="glass reveal">
              <span className="tag">ACCOUNT</span>
              <h3>{t("site_trust1_t")}</h3>
              <p>{t("site_trust1_d")}</p>
            </article>
            <article className="glass reveal delay1">
              <span className="tag">CARD</span>
              <h3>{t("site_trust2_t")}</h3>
              <p>{t("site_trust2_d")}</p>
            </article>
            <article className="glass reveal delay2">
              <span className="tag">INVOICE</span>
              <h3>{t("site_trust3_t")}</h3>
              <p>{t("site_trust3_d")}</p>
            </article>
          </div>
        </section>

        <section id="how" className="section alt">
          <div className="container process-grid">
            <div className="process-copy reveal">
              <span className="kicker">{t("site_how_kicker")}</span>
              <h2>{t("site_how_h2")}</h2>
              <p>{t("site_how_p")}</p>
            </div>

            <div>
              <div className="steps">
                {(["site_how_s1", "site_how_s2", "site_how_s3", "site_how_s4", "site_how_s5", "site_how_s6", "site_how_s7"] as const).map(
                  (k, i) => (
                    <div className="step reveal" key={k}>
                      <b>{i + 1}</b>
                      <p>{t(k)}</p>
                    </div>
                  ),
                )}
              </div>

              <div className="kyc-stage reveal">
                <h3>KYC / ACCOUNT OPENING</h3>
                <p>Client verification</p>
                <div className="kyc-row">
                  <span>IDENTITY</span>
                  <b>VERIFIED</b>
                </div>
                <div className="kyc-row">
                  <span>DOCUMENTS</span>
                  <b>SIGNED</b>
                </div>
                <div className="kyc-row">
                  <span>ACCOUNT</span>
                  <b>ACTIVE</b>
                </div>
                <div className="flowline">
                  <span>01&nbsp;&nbsp;KYC</span>
                  <i />
                  <span>02&nbsp;&nbsp;ACCOUNT</span>
                  <i />
                  <span>03</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container account-grid">
            <div className="reveal">
              <span className="kicker">{t("site_acc_kicker")}</span>
              <h2>{t("site_acc_h2")}</h2>
              <div className="benefits">
                {(
                  [
                    ["site_acc_b1_t", "site_acc_b1_d"],
                    ["site_acc_b2_t", "site_acc_b2_d"],
                    ["site_acc_b3_t", "site_acc_b3_d"],
                    ["site_acc_b4_t", "site_acc_b4_d"],
                  ] as const
                ).map(([tk, dk], i) => (
                  <div className="benefit" key={tk}>
                    <b>{i + 1}</b>
                    <div>
                      <h3>{t(tk)}</h3>
                      <p>{t(dk)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="account-scene reveal delay1" data-tilt-zone>
              <div className="mapmesh" />
              <div className="arc a1" />
              <div className="arc a2" />
              <div className="account-console tilt">
                <div className="ui-brand">TTEX ACCOUNT</div>
                <span className="ui-label">Available balance</span>
                <strong className="balance">₽ 1 250 000</strong>
                <div className="currency">
                  <span>USD</span>
                  <b>13 420.00</b>
                </div>
                <div className="currency">
                  <span>EUR</span>
                  <b>4 880.00</b>
                </div>
                <div className="currency">
                  <span>CNY</span>
                  <b>38 600.00</b>
                </div>
                <a className="btn primary" href={BOT_LINK} target="_blank" rel="noreferrer">
                  CREATE PAYMENT
                </a>
              </div>
            </div>
          </div>
        </section>

        <section id="calculator" className="section alt">
          <div className="container calc-wrap">
            <div className="calc-copy reveal">
              <span className="kicker">{t("site_calc_kicker")}</span>
              <h2>{t("site_calc_h2")}</h2>
              <p>{t("site_calc_p1")}</p>
              <p>{t("site_calc_p2")}</p>
            </div>
            <Calculator rate={rate} t={t} />
          </div>
        </section>

        <section id="payments" className="section">
          <div className="container section-head reveal">
            <span className="kicker">{t("site_pay_kicker")}</span>
            <h2>{t("site_pay_h2")}</h2>
            <p>{t("site_pay_p")}</p>
          </div>
          <div className="container payment-grid">
            <article className="payment reveal">
              <span className="tag">INVOICE</span>
              <h3>{t("site_pay1_t")}</h3>
              <p>{t("site_pay1_d")}</p>
              <div className="receipt">
                <small>INVOICE</small>
                <strong>USD 25,000.00</strong>
                <em>PAID</em>
              </div>
            </article>
            <article className="payment reveal delay1">
              <span className="tag">SWIFT</span>
              <h3>{t("site_pay2_t")}</h3>
              <p>{t("site_pay2_d")}</p>
              <div className="receipt">
                <small>SWIFT TRANSFER</small>
                <strong>USD 25,000.00</strong>
                <em>COMPLETED ✓</em>
              </div>
            </article>
            <article className="payment reveal delay2">
              <span className="tag">CARD</span>
              <h3>{t("site_pay3_t")}</h3>
              <p>{t("site_pay3_d")}</p>
              <div className="mini-card">
                <span>•••• 4821</span>
                <b>＋</b>
              </div>
            </article>
          </div>
        </section>

        <section id="payout" className="section alt">
          <div className="container payout-grid">
            <div className="reveal">
              <span className="kicker">{t("site_payout_kicker")}</span>
              <h2>{t("site_payout_h2")}</h2>
              <div className="steps">
                {(["site_payout_s1", "site_payout_s2", "site_payout_s3", "site_payout_s4"] as const).map((k, i) => (
                  <div className="step" key={k}>
                    <b>{i + 1}</b>
                    <p>{t(k)}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="reveal delay1">
              <div className="payout-card">
                <span className="kicker">PAYOUT</span>
                <p>Foreign card → TTEX → Bank РФ</p>
                <div className="exchange">
                  <strong>1 000 USD</strong>
                  <span>→</span>
                  <strong>78 450 ₽</strong>
                </div>
                <div className="divider" />
                <div className="meta">
                  <span>Status</span>
                  <b>Completed ✓</b>
                </div>
                <div className="meta">
                  <span>Destination</span>
                  <b>Bank РФ</b>
                </div>
                <div className="meta">
                  <span>Documents</span>
                  <b>Available</b>
                </div>
              </div>
              <div className="payout-rail">
                <span>$</span>
                <i />
                <span>T</span>
                <i />
                <span>₽</span>
              </div>
            </div>
          </div>
        </section>

        <section id="card" className="section">
          <div className="container card-showcase">
            <div className="signature-card reveal tilt">
              <div className="ui-brand">TTEX</div>
              <span>INTERNATIONAL</span>
              <div className="chip" />
              <strong>•••• 4821</strong>
              <small>VIRTUAL / PHYSICAL</small>
            </div>
            <div className="reveal delay1">
              <span className="kicker">{t("site_card_kicker")}</span>
              <h2>{t("site_card_h2")}</h2>
              <span className="tag">{t("site_card_soon")}</span>
            </div>
            <div className="usecases reveal delay2">
              <div className="usecase">
                ◎<small>{t("site_uc1")}</small>
              </div>
              <div className="usecase">
                ▱<small>{t("site_uc2")}</small>
              </div>
              <div className="usecase">
                ▭<small>{t("site_uc3")}</small>
              </div>
              <div className="usecase">
                ✈<small>{t("site_uc4")}</small>
              </div>
              <div className="usecase">
                ▦<small>{t("site_uc5")}</small>
              </div>
            </div>
          </div>
        </section>

        <section id="cta" className="section final-cta">
          <div className="container cta reveal">
            <div>
              <span className="kicker">TTEX</span>
              <h2>{t("site_cta_h2")}</h2>
              <p>{t("site_cta_p")}</p>
              <a className="url" href={SITE_URL}>
                ttex.online
              </a>
            </div>
            <div className="flow">
              <span>BOT / SITE</span>
              <i>→</i>
              <span>KYC</span>
              <i>→</i>
              <span>ACCOUNT</span>
              <i>→</i>
              <span>PAYMENT</span>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div className="container foot">
          <span>TTEX</span>
          <span>{t("site_footer_tagline")}</span>
          <span>© 2026</span>
        </div>
      </footer>
    </div>
  );
}

function Calculator({ rate, t }: { rate: RateInfo | null; t: (k: DictKey) => string }) {
  const r = rate?.rate ?? null;
  const [rub, setRub] = useState("10000");

  const usd = (() => {
    const num = parseFloat(rub.replace(",", "."));
    if (!rub || Number.isNaN(num) || r === null || r === 0) return "—";
    return (num / r).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  })();

  return (
    <div className="calc-card reveal delay1">
      <div className="calc-head">
        <h3>{t("site_calc_kicker")}</h3>
        <span className="pair">USD / RUB</span>
      </div>
      <div className="calc-field">
        <div className="calc-label">
          <span>{t("site_calc_pay")}</span>
          <strong>{t("site_calc_pay_unit")}</strong>
        </div>
        <input
          className="calc-input"
          type="number"
          min="0"
          step="100"
          inputMode="decimal"
          value={rub}
          onChange={(e) => setRub(e.target.value)}
        />
      </div>
      <div className="calc-switch">↓</div>
      <div className="calc-field">
        <div className="calc-label">
          <span>{t("site_calc_get")}</span>
          <strong>USD</strong>
        </div>
        <div className="calc-output">{usd}</div>
      </div>
      <div className="rate">
        {r === null ? (
          t("rate_loading")
        ) : (
          <>
            1 USD ≈ <b>{r.toFixed(2)} ₽</b>
          </>
        )}
      </div>
      <button
        className="calc-start"
        type="button"
        onClick={() => document.querySelector("#cta")?.scrollIntoView({ behavior: "smooth" })}
      >
        {t("site_calc_start")}
      </button>
      <div className="calc-meta">
        <div>
          <strong>≈ 2 min</strong>
          <span>{t("site_calc_m1_l")}</span>
        </div>
        <div>
          <strong>&lt; 10 min</strong>
          <span>{t("site_calc_m2_l")}</span>
        </div>
      </div>
    </div>
  );
}
